import { useTranslation } from 'react-i18next';
/**
 * LabourCostTimelineChart.js
 *
 * Stacked area chart of labour cost over time for a single project,
 * sliced per team member, with vertical markers wherever someone's
 * hourly rate changed. Powered by /api/projects/{pid}/labour-cost-timeline.
 *
 * Lives on the project budget card so a manager can explain margin
 * shifts at a glance: the layer thickness tells you who's burning
 * the most each month, and a vertical line tells you exactly when
 * a raise hit the books.
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, Legend,
  CartesianGrid, ReferenceLine,
} from 'recharts';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { LineChart as LineChartIcon, RefreshCw } from 'lucide-react';
import { useDisplayCurrency, SYMBOL } from '../hooks/useDisplayCurrency';

const API = process.env.REACT_APP_BACKEND_URL;

// Pleasant, distinguishable, color-blind-friendlier palette
const PALETTE = [
  '#2563EB', '#DC2626', '#16A34A', '#D97706', '#7C3AED',
  '#0891B2', '#DB2777', '#65A30D', '#9333EA', '#EA580C',
  '#0EA5E9', '#84CC16', '#F59E0B', '#EF4444', '#14B8A6',
];

const fmtMoney = (v, cur) => {
  if (v == null) return '—';
  const num = Number(v).toLocaleString('pl-PL', { maximumFractionDigits: 0 });
  if (cur === 'PLN') return `${num} zł`;
  return `${SYMBOL[cur] || ''}${num}`;
};

export default function LabourCostTimelineChart({ projectId, projectName }) {
  const { t } = useTranslation();
  const { currency: displayCurrency } = useDisplayCurrency();
  const [granularity, setGranularity] = useState('month');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hiddenEmps, setHiddenEmps] = useState(() => new Set());

  const reload = async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/projects/${projectId}/labour-cost-timeline`,
        { params: { granularity, display_currency: displayCurrency } });
      setData(r.data);
    } catch (_e) {
      setData({ timeline: [], employees: [], rate_changes: [], currency: displayCurrency });
    } finally { setLoading(false); }
  };

  // Reload when project, granularity OR the global display currency changes
  useEffect(() => { reload(); }, [projectId, granularity, displayCurrency]); // eslint-disable-line

  const colorFor = useMemo(() => {
    const m = {};
    (data?.employees || []).forEach((e, i) => { m[e.id] = PALETTE[i % PALETTE.length]; });
    return m;
  }, [data?.employees]);

  // Build {period, totalsByEmployee, total} rows for recharts
  const rows = useMemo(() => {
    return (data?.timeline || []).map((r) => {
      const out = { period: r.period };
      let total = 0;
      for (const e of (data?.employees || [])) {
        if (hiddenEmps.has(e.id)) continue;
        const v = r[e.id] || 0;
        out[e.id] = v;
        total += v;
      }
      out.__total = total;
      return out;
    });
  }, [data, hiddenEmps]);

  const grandTotal = rows.reduce((s, r) => s + (r.__total || 0), 0);
  const visibleEmps = (data?.employees || []).filter((e) => !hiddenEmps.has(e.id));
  const cur = data?.currency || displayCurrency;

  // Rate-change markers: keep only those that land inside a bucket we
  // actually have data for, so we don't dangle lines off-axis.
  const periodSet = new Set(rows.map((r) => r.period));
  const periodKeyOf = (iso) => {
    if (!iso) return null;
    const d = new Date(iso);
    if (Number.isNaN(d)) return null;
    if (granularity === 'month') return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
    // ISO week
    const tmp = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
    tmp.setUTCDate(tmp.getUTCDate() + 4 - (tmp.getUTCDay() || 7));
    const yearStart = new Date(Date.UTC(tmp.getUTCFullYear(), 0, 1));
    const week = Math.ceil((((tmp - yearStart) / 86400000) + 1) / 7);
    return `${tmp.getUTCFullYear()}-W${String(week).padStart(2, '0')}`;
  };
  const markers = (data?.rate_changes || [])
    .map((c) => ({ ...c, period: periodKeyOf(c.effective_from) }))
    .filter((c) => c.period && periodSet.has(c.period));

  const toggleEmp = (id) => {
    setHiddenEmps((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  if (loading && !data) {
    return <div className="text-sm text-slate-400 py-6 text-center">{t('auto.misc2.loadingLabourCostTimeline')}</div>;
  }
  if (!data || rows.length === 0) {
    return (
      <div className="text-sm text-slate-400 py-6 text-center border rounded bg-slate-50">
        {t('auto.misc2.noTimesheetDataForThisProject')}
      </div>
    );
  }

  return (
    <div data-testid="labour-cost-timeline" className="space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2 text-sm font-medium">
          <LineChartIcon className="h-4 w-4 text-blue-600" />
          Labour cost over time — {projectName || 'project'}
          <Badge variant="outline" className="ml-2 font-normal text-[11px]">
            Total {fmtMoney(grandTotal, cur)}
          </Badge>
        </div>
        <div className="flex items-center gap-1">
          <Button size="sm" variant={granularity === 'week' ? 'default' : 'outline'}
            onClick={() => setGranularity('week')} className="h-7 text-xs"
            data-testid="timeline-week-btn">{t('auto.misc2.weekly')}</Button>
          <Button size="sm" variant={granularity === 'month' ? 'default' : 'outline'}
            onClick={() => setGranularity('month')} className="h-7 text-xs"
            data-testid="timeline-month-btn">{t('auto.misc2.monthly')}</Button>
          <Button size="icon" variant="ghost" onClick={reload} className="h-7 w-7"
            title={t('auto.misc2.reload')} data-testid="timeline-reload-btn">
            <RefreshCw className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      <div style={{ width: '100%', height: 320 }}>
        <ResponsiveContainer>
          <AreaChart data={rows} margin={{ top: 10, right: 12, left: 0, bottom: 6 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
            <XAxis dataKey="period" fontSize={11} stroke="#6B7280" />
            <YAxis fontSize={11} stroke="#6B7280" tickFormatter={(v) => fmtMoney(v, cur)} width={80} />
            <Tooltip
              formatter={(v, key) => {
                const emp = (data.employees || []).find((e) => e.id === key);
                return [fmtMoney(v, cur), emp?.name || key];
              }}
              labelFormatter={(label) => `Period: ${label}`}
              contentStyle={{ fontSize: 12 }}
            />
            <Legend onClick={(o) => toggleEmp(o.dataKey)} wrapperStyle={{ fontSize: 11, paddingTop: 6 }} />
            {visibleEmps.map((e) => (
              <Area
                key={e.id}
                type="monotone"
                dataKey={e.id}
                name={e.name}
                stackId="1"
                stroke={colorFor[e.id]}
                fill={colorFor[e.id]}
                fillOpacity={0.55}
              />
            ))}
            {markers.map((m, idx) => (
              <ReferenceLine
                key={`rc-${idx}`}
                x={m.period}
                stroke={colorFor[m.employee_id] || '#9CA3AF'}
                strokeDasharray="4 3"
                label={{
                  value: `${m.employee_name}: ${m.from_rate}→${m.to_rate}`,
                  position: 'insideTopRight',
                  fill: colorFor[m.employee_id] || '#374151',
                  fontSize: 9,
                }}
              />
            ))}
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {markers.length > 0 && (
        <div className="text-[11px] text-slate-500 flex items-start gap-2">
          <span className="font-semibold">{t('auto.misc2.rateChangesInView')}</span>
          <span className="flex flex-wrap gap-x-2 gap-y-0.5">
            {markers.map((m, i) => (
              <span key={i} className="whitespace-nowrap">
                <span className="inline-block w-2 h-2 rounded-full mr-1 align-middle"
                  style={{ background: colorFor[m.employee_id] || '#9CA3AF' }} />
                {m.employee_name}: {m.from_rate}→{m.to_rate} {m.currency} on{' '}
                <span className="font-mono">{m.effective_from}</span>
                {m.note ? <span className="italic"> · {m.note}</span> : ''}
              </span>
            ))}
          </span>
        </div>
      )}
      <div className="text-[10px] text-slate-400">
        {t('auto.misc2.costsAreCalculatedInPlnPer')}
        <span className="font-semibold mx-1">{cur}</span>
        {t('auto.misc2.usingTodaySNbpRateSwitch')}
      </div>
    </div>
  );
}
